import React, { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { devToolsEnabled } from 'constants/devtools';
import {
  INVITE_ADMIN_ACTION,
  INVITE_CREATOR_ACTION,
  INVITE_CREATOR_LITE_ACTION,
  INVITE_VIEWER_ACTION,
} from 'constants/scopes';
import { SHOW_GET_STARTED_CHECKLIST } from 'constants/triggers';
import { completeTriggerCreator } from 'creators/triggers';
import { selectCurrentUser } from 'selectors/user';
import { platformDetails } from 'utilities/device';

import { useShouldShowTrigger } from 'hooks/triggers';
import { useHasSomeScopes } from 'hooks/useHasScopes';
import { useMatchMobileOnly } from 'hooks/useMatchMedia';

import { useInviteCalloutDismissed } from '../invite-callout';

import { MAX_ACCOUNT_AGE } from 'components/layout/navigation/get-started-checklist';

const InviteCallout = React.lazy(() => import('../invite-callout'));
const GetStartedChecklist = React.lazy(() =>
  import('../get-started-checklist')
);

export const CALLOUTS = {
  INVITE_CALLOUT: 'INVITE_CALLOUT',
  GET_STARTED_CHECKLIST_CALLOUT: 'GET_STARTED_CHECKLIST_CALLOUT',
};

export function SidebarCallout({ displayMode }) {
  const callout = useSidebarCallout();

  switch (callout) {
    case CALLOUTS.INVITE_CALLOUT:
      return (
        <Suspense fallback={null}>
          <InviteCallout displayMode={displayMode} />
        </Suspense>
      );
    case CALLOUTS.GET_STARTED_CHECKLIST_CALLOUT:
      return (
        <Suspense fallback={null}>
          <GetStartedChecklist displayMode={displayMode} />
        </Suspense>
      );
    default:
      return null;
  }
}

const urlParams = new URLSearchParams(window.location.search);

export function useSidebarCallout() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const isMobileScreenSize = useMatchMobileOnly();
  const hasInviteCalloutScope = useHasSomeScopes([
    INVITE_VIEWER_ACTION,
    INVITE_CREATOR_ACTION,
    INVITE_ADMIN_ACTION,
    INVITE_CREATOR_LITE_ACTION,
  ]);

  const shouldShowGetStartedChecklist = useShouldShowTrigger(
    SHOW_GET_STARTED_CHECKLIST
  );
  const [inviteCalloutDisabled] = useInviteCalloutDismissed();

  const canShowInviteCallout = !inviteCalloutDisabled && hasInviteCalloutScope;

  if (devToolsEnabled) {
    const param = urlParams.get('sidebar-callout');

    if (param && CALLOUTS[param]) {
      return param;
    }
  }

  if (shouldShowGetStartedChecklist) {
    const createdAt = new Date(user.createdAt).getDate();
    const currentDate = new Date().getDate();

    if (currentDate - createdAt > MAX_ACCOUNT_AGE) {
      dispatch(completeTriggerCreator(SHOW_GET_STARTED_CHECKLIST));
    } else if (platformDetails.type === 'desktop') {
      return isMobileScreenSize ? null : CALLOUTS.GET_STARTED_CHECKLIST_CALLOUT;
    }
  }

  return canShowInviteCallout ? CALLOUTS.INVITE_CALLOUT : null;
}
